import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import {
  createRouter,
  createWebHistory,
  NavigationFailure,
  RouteLocationRaw,
  RouterView,
  _RouteLocationBase,
} from 'vue-router';
import account from './resources/account';
import system from './resources/system';
import { I18nParamKey } from '@/router/types';
import { checkI18nParamKey, convertI18nKeyToI18nParamKey } from '@/utils/route';
import { convertI18nParamKeyToI18nKey, getI18nKeyByBrowser } from '@/utils/i18n';
import { authCheck } from '@/utils/router';
import { useAppStore } from '@/store/resources/app';
import i18n from '@/i18n';

// 记录至少访问过一次的路由名称
const accessedRouteNames: _RouteLocationBase['name'][] = [];

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Root',
      component: RouterView,
    },
    {
      path: '/:i18nParamKey',
      name: 'I18n',
      component: RouterView,
      redirect: (to) => {
        // const accessToken = localStorage.getItem('accessToken');
        return {
          ...to,
          // name: accessToken ? 'SystemHome' : 'AccountSignIn',
          name: 'SystemHome',
        };
      },
    },
    ...account,
    ...system,
    {
      path: '/:i18nParamKey/error/not-found',
      name: 'NotFound',
      component: () =>
        import(/* webpackChunkName: "not-found" */ '@/views/error/not-found/NotFound.vue'),
    },
  ],
  // 滚动条处理
  async scrollBehavior(to, from, savedPosition) {
    // 此页面重来没有被访问过, 滚动条重置到 0
    if (!accessedRouteNames.includes(to.name)) {
      accessedRouteNames.push(to.name);

      return {
        left: 0,
        top: 0,
      };
    }

    // 同一个页面, 不需要重置(例如某些列表页面条件筛选的时候)
    if (to.name === from.name) {
      return false;
    }

    // 返回上一页的时候, 重置到原来保留的位置
    if (savedPosition) {
      return savedPosition;
    }

    // push 到新页面, 滚动条重置到 0
    return {
      left: 0,
      top: 0,
    };
  },
});

// NProgress 配置
NProgress.configure({
  showSpinner: false,
  easing: 'ease-in-out',
  minimum: 0.25,
  speed: 250,
  trickleSpeed: 65,
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const appStore = useAppStore();

  if (
    appStore.initDataComplete &&
    (to.params.i18nParamKey !== from.params.i18nParamKey || to.name !== from.name)
  ) {
    // 显示顶部进度条
    // 只有当切换多语言或者不同页面之间跳转才显示
    NProgress.start();
  }

  const i18nParamKey = to.params.i18nParamKey as I18nParamKey;

  // 1. 初始化 ip 对应的多语言 key
  if (!appStore.initDataComplete) {
    await appStore.updateI18nKeyByIpAsync();
  }

  // 2. 404 处理
  // 没有 name 就代表找不到路由
  if (!to.name) {
    const currentURLI18nParamKey = to.path.split('/')[1] as I18nParamKey;
    // 从当前 url 获取多语言参数然后跳转到 /zh/not-found, /en/not-found ...
    // url 的多语言有问题, 就根据 ip 和浏览器获取
    return next({
      replace: true,
      params: {
        i18nParamKey: checkI18nParamKey(currentURLI18nParamKey)
          ? currentURLI18nParamKey
          : convertI18nKeyToI18nParamKey(appStore.i18nKeyByIp || getI18nKeyByBrowser()),
      },
      name: 'NotFound',
    });
  }

  // 3. 防止 url 中的多语言不合法(用户乱输入例如 zh-abc)
  if (!checkI18nParamKey(i18nParamKey)) {
    // 不合法的 url 将会跳转到
    //  - 1. ip 对应的多语言 key (后端有返回区号)
    //  - 2. 浏览器对应的多语言 key (后端没有返回区号)

    return next(`/${convertI18nKeyToI18nParamKey(appStore.i18nKeyByIp || getI18nKeyByBrowser())}`);
  }

  // 4. 根据 url 中的多语言加载语言包
  if (convertI18nParamKeyToI18nKey(i18nParamKey) !== i18n.global.locale) {
    await appStore.updateI18nKeyAsync(convertI18nParamKeyToI18nKey(i18nParamKey));
  }

  // 5. 登录状态校验(部分路由需要登录后才能访问)
  await authCheck(to, next);

  // 6. 标记 store 初始化数据已完成, 可以开始进行渲染
  if (!appStore.initDataComplete) {
    appStore.updateInitDataComplete(true);
  }
});

// 路由守卫
router.afterEach(() => {
  const appStore = useAppStore();

  if (appStore.initDataComplete) {
    setTimeout(() => NProgress.done(), 100);
  }
});

// 每次调用 router.push 的时候, 过滤掉空的 query 参数
const originRouterPush = router.push;
router.push = function (params: RouteLocationRaw): Promise<void | NavigationFailure | undefined> {
  if (typeof params === 'object') {
    params.query = Object.fromEntries(
      Object.entries(typeof params.query === 'object' ? params.query : {}).filter(
        ([key, value]) => !!key && !!value
      )
    );
  }
  return originRouterPush(params);
};

export default router;
